// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Time and client" for an agent chat (design/agents-side-bar r2-t1..t3): how long the agent
// worked on the chat, who it is for (client, ClickUp task, Billability), that client's agent time
// this week, and what of it was logged to the task. Logging is never automatic: only the person's
// "Log time" press writes a ClickUp time entry. The arithmetic lives in AgentTimeModel.ts.
//
// Task linking (design/agents-task-linking, 2026-10-16): a chat can carry several ClickUp tasks and
// each turn's time goes to exactly one of them (AgentTaskLinkModel.ts). Konvo links a task itself
// when the chat names it (`via: 'auto'`, with `why`), and otherwise suggests up to three open tasks
// (`suggest`). The single-task fields (`task`, `taskVia`, `logged`, `unloggedMs`) stay for older
// apps: they describe the chat's first task and the chat as a whole.

import {z} from 'zod';

/**
 * A client as agent time keeps it: `client:<id>` (a saved client), `space:<id>` (a ClickUp space)
 * or `name:<name>` (a name from the task, or typed in). Week totals group by the name.
 */
export const AgentTimeClientSchema = z.object({
	id: z
		.string()
		.trim()
		.min(1)
		.max(200)
		.regex(/^(client|space|name):[^\n]{1,190}$/),
	name: z.string().trim().min(1).max(120),
});
export type AgentTimeClient = z.infer<typeof AgentTimeClientSchema>;

/** Where the chat's client came from: picked for the chat, from its ClickUp task, or picked for the project. */
export const AGENT_TIME_CLIENT_VIA = ['chat', 'task', 'project'] as const;
export type AgentTimeClientVia = (typeof AGENT_TIME_CLIENT_VIA)[number];

export const AgentTimeTaskSchema = z.object({
	id: z.string().max(60),
	name: z.string().max(500),
	customId: z.string().max(60).nullable(),
	url: z.string().max(500).nullable(),
	status: z
		.object({
			name: z.string().max(100),
			color: z.string().max(20).nullable(),
			/** ClickUp's status type (open, custom, done, closed), with the person's "counts as done" statuses as done. */
			type: z.string().max(20).nullable().optional(),
			/** How far along its list the status is, 0 (open) to 1 (done), for the status ring. */
			fill: z.number().min(0).max(1).nullable().optional(),
		})
		.nullable(),
	/** The task's Billability custom field, e.g. "Billable". */
	billability: z.string().max(200).nullable(),
	list: z.string().max(200).nullable(),
	space: z.string().max(200).nullable(),
});
export type AgentTimeTask = z.infer<typeof AgentTimeTaskSchema>;

/**
 * How a task came to the chat: sent from a ClickUp task (`source`), linked by the person (the panel,
 * the corner assistant, Konvo Assistant) or by Konvo itself because the chat names it (`auto`).
 */
export const AGENT_TIME_TASK_VIA = ['source', 'picked', 'auto'] as const;
export type AgentTimeTaskVia = (typeof AGENT_TIME_TASK_VIA)[number];

/** Why Konvo linked a task by itself ("Konvo linked it: your first prompt has the task's link…"). */
export const AgentTaskLinkWhySchema = z.object({
	/** The turn (1-based) whose prompt has the task's link or ID. */
	prompt: z.number().int().positive().nullable(),
	/** The chat's branch, when it names the task. */
	branch: z.string().max(300).nullable(),
	/** The pull request whose title or description names the task. */
	pr: z.number().int().positive().nullable(),
	/** Nothing named it, but the model was sure: its short reason. */
	model: z.string().max(300).nullable(),
});
export type AgentTaskLinkWhy = z.infer<typeof AgentTaskLinkWhySchema>;

const TimeLoggedSchema = z.object({
	ms: z.number().nonnegative(),
	youMinutes: z.number().int().nonnegative(),
	at: z.string().max(40),
	entries: z.number().int().nonnegative(),
});

/** One of the chat's tasks, with the turns that count for it and its share of the time. */
export const AgentTimeTaskShareSchema = z.object({
	task: AgentTimeTaskSchema,
	via: z.enum(AGENT_TIME_TASK_VIA),
	why: AgentTaskLinkWhySchema.nullable(),
	/** The turns (1-based prompt numbers) whose time goes to this task. */
	turns: z.array(z.number().int().nonnegative()).max(400),
	workedMs: z.number().nonnegative(),
	/** The person's minutes on those turns (estimated, or their share of the minutes they set). */
	youMinutes: z.number().int().nonnegative(),
	unloggedMs: z.number().nonnegative(),
	youUnloggedMinutes: z.number().int().nonnegative(),
	logged: TimeLoggedSchema.nullable(),
});
export type AgentTimeTaskShare = z.infer<typeof AgentTimeTaskShareSchema>;

/** A turn in the Turns view: when, how long, and the task its time goes to. Never the prompt itself. */
export const AgentTimeTurnRowSchema = z.object({
	id: z.string().max(200),
	/** 1-based prompt number; 0 for the tail of an older turn Konvo did not read. */
	n: z.number().int().nonnegative(),
	at: z.string().max(40).nullable(),
	ms: z.number().nonnegative(),
	taskId: z.string().max(60).nullable(),
	/** Its time was logged to ClickUp: it stays with its task. */
	locked: z.boolean(),
	/** Konvo moved it: the person started on something else. */
	moved: z.boolean(),
});
export type AgentTimeTurnRow = z.infer<typeof AgentTimeTurnRowSchema>;

/** One of the tasks Konvo suggests for the chat, with a short reason. */
export const AgentTaskSuggestionSchema = z.object({
	task: AgentTimeTaskSchema,
	reason: z.string().max(300),
	/** The one Konvo thinks most likely (outlined). */
	best: z.boolean(),
	/** The turns Konvo thinks are about it. */
	turns: z.array(z.number().int().nonnegative()).max(400),
});
export type AgentTaskSuggestion = z.infer<typeof AgentTaskSuggestionSchema>;

export const AgentTaskSuggestSchema = z.object({
	/** `looking`: Konvo is reading the chat; ask again in a few seconds. */
	status: z.enum(['looking', 'ready']),
	options: z.array(AgentTaskSuggestionSchema).max(3),
	/** The chat covered two of them: "Both, split by turn". */
	split: z.boolean(),
	/** "None of these": not offered again for this chat. */
	dismissed: z.boolean(),
});
export type AgentTaskSuggest = z.infer<typeof AgentTaskSuggestSchema>;

export const AgentTimeDaySchema = z.object({
	date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	ms: z.number().nonnegative(),
});
export type AgentTimeDay = z.infer<typeof AgentTimeDaySchema>;

/**
 * The knobs of the estimate of the person's own minutes on a chat (AgentTimeModel.youTurnMinutes):
 * kept per person, they apply to every chat whose minutes the person has not set by hand.
 */
export const AgentEstimateKnobsSchema = z.object({
	/** Minutes per prompt: reading the agent's reply and deciding what to ask next. */
	promptMinutes: z.number().min(0).max(30),
	/** How fast the person writes prompts, in words a minute. */
	wordsPerMinute: z.number().min(5).max(200),
	/** Minutes per question answered or approval given. */
	answerMinutes: z.number().min(0).max(30),
	/** Minutes to review 100 changed lines (added plus removed) of the agent's code. */
	reviewMinutesPer100Lines: z.number().min(0).max(120),
});
export type AgentEstimateKnobs = z.infer<typeof AgentEstimateKnobsSchema>;

/** The estimate's knobs until the person tweaks them. */
export const AGENT_ESTIMATE_DEFAULTS: AgentEstimateKnobs = {
	promptMinutes: 1,
	wordsPerMinute: 30,
	answerMinutes: 0.5,
	reviewMinutesPer100Lines: 4,
};

/** What the estimate of the person's minutes rests on, for "How it's estimated". */
export const AgentYouBasisSchema = z.object({
	/** Prompts the person wrote (answers to questions are not prompts). */
	prompts: z.number().int().nonnegative(),
	/** Words in those prompts (at most AGENT_YOU_WORDS_PER_PROMPT each: longer is pasted, not typed). */
	words: z.number().int().nonnegative(),
	/** Questions the person answered. */
	answers: z.number().int().nonnegative(),
	/** Approvals the person gave from Konvo. */
	approvals: z.number().int().nonnegative(),
	/** Lines the agent changed (added plus removed; at most AGENT_YOU_LINES_PER_TURN a turn). */
	lines: z.number().int().nonnegative(),
});
export type AgentYouBasis = z.infer<typeof AgentYouBasisSchema>;

/** The person's own minutes on the chat: estimated from what Konvo knows, or set by them. */
export const AgentYouTimeSchema = z.object({
	/** What counts (the week, the digest, Log time): theirs when edited, else the estimate. */
	minutes: z.number().int().nonnegative(),
	estimateMinutes: z.number().int().nonnegative(),
	/** The person set the minutes for this chat. */
	edited: z.boolean(),
	basis: AgentYouBasisSchema,
	/** The person's knobs (the defaults until tweaked). */
	knobs: AgentEstimateKnobsSchema,
	/** Their minutes already logged to the task from here. */
	loggedMinutes: z.number().int().nonnegative(),
	/** Their minutes not logged yet (what Log time offers). */
	unloggedMinutes: z.number().int().nonnegative(),
});
export type AgentYouTime = z.infer<typeof AgentYouTimeSchema>;

export const AgentTimeViewSchema = z.object({
	/** The agent's working time on the chat: each turn from the prompt to its last step. */
	workedMs: z.number().nonnegative(),
	/** Turns the person started (their prompts). */
	turns: z.number().int().nonnegative(),
	/** A turn is running now; workedMs counts it up to computedAt. */
	running: z.boolean(),
	/** The chat was longer than Konvo reads: its oldest turns are not counted. */
	partial: z.boolean(),
	computedAt: z.string().max(40).nullable(),
	/** The machine could not be read: these are the numbers from the last read. */
	stale: z.boolean(),
	projectId: z.string().max(200).nullable(),
	client: AgentTimeClientSchema.extend({via: z.enum(AGENT_TIME_CLIENT_VIA)}).nullable(),
	/** The chat's first task (older apps show only this one). */
	task: AgentTimeTaskSchema.nullable(),
	/** The task came with the chat (sent from ClickUp) or was linked (by the person or Konvo). */
	taskVia: z.enum(['source', 'picked']).nullable(),
	/** Agent time (ms) and the person's minutes already logged from here, over all the tasks. */
	logged: TimeLoggedSchema.nullable(),
	/** Agent time not logged yet (what Log time would log). */
	unloggedMs: z.number().nonnegative(),
	/** The chat's tasks, first linked first (`task` is the first). */
	tasks: z.array(AgentTimeTaskShareSchema).max(10),
	/** The chat's turns, oldest first, with the task each one's time goes to. */
	turnTasks: z.array(AgentTimeTurnRowSchema).max(400),
	/** What Konvo suggests when it is not sure; null before it looked. */
	suggest: AgentTaskSuggestSchema.nullable(),
	/** The person's own minutes on the chat (estimated or theirs). */
	you: AgentYouTimeSchema,
	/**
	 * The client's agent time this week, Monday to Sunday, across the person's chats, and beside it
	 * the person's own minutes (youMinutes, estimated unless they set them).
	 */
	week: z
		.object({
			client: z.string().max(120),
			days: z.array(AgentTimeDaySchema.extend({youMinutes: z.number().int().nonnegative()})).length(7),
			totalMs: z.number().nonnegative(),
			youMinutes: z.number().int().nonnegative(),
			chats: z.number().int().nonnegative(),
		})
		.nullable(),
	/** Whether the person's ClickUp is connected (needed to log). */
	clickup: z.enum(['connected', 'not_connected']),
});
export type AgentTimeView = z.infer<typeof AgentTimeViewSchema>;

/** Pick the chat's client, for the chat alone or every chat in its project; null clears that choice. */
export const AgentTimeClientRequestSchema = z.object({
	client: AgentTimeClientSchema.nullable(),
	scope: z.enum(['chat', 'project']),
	tz: z.string().max(64).optional(),
});
export type AgentTimeClientRequest = z.infer<typeof AgentTimeClientRequestSchema>;

const TaskIdSchema = z
	.string()
	.trim()
	.min(1)
	.max(60)
	.regex(/^[A-Za-z0-9_-]+$/);
const TurnIdSchema = z.string().min(1).max(200);

/** Older apps: link a ClickUp task to the chat (null unlinks the tasks without logged time). */
export const AgentTimeTaskRequestSchema = z.object({
	taskId: TaskIdSchema.nullable(),
	tz: z.string().max(64).optional(),
});
export type AgentTimeTaskRequest = z.infer<typeof AgentTimeTaskRequestSchema>;

/**
 * Change the chat's tasks (none of these touch ClickUp):
 * - `link`: add a task (its turns: from `fromTurn` on, else what Konvo sees is about it);
 * - `unlink`: take one off ("Not this one" shows the suggestions again); not one with logged time;
 * - `move`: a turn's time to another task (`after`: that turn and every one after it), linking it
 *   when it is new; turns logged to ClickUp stay;
 * - `both`: link two or three of Konvo's suggestions, the turns split by what each prompt was about;
 * - `dismiss`: "None of these".
 */
export const AgentTimeTasksRequestSchema = z.discriminatedUnion('op', [
	z.object({
		op: z.literal('link'),
		taskId: TaskIdSchema,
		fromTurn: TurnIdSchema.optional(),
		tz: z.string().max(64).optional(),
	}),
	z.object({op: z.literal('unlink'), taskId: TaskIdSchema, tz: z.string().max(64).optional()}),
	z.object({
		op: z.literal('move'),
		turnId: TurnIdSchema,
		taskId: TaskIdSchema,
		after: z.boolean().optional(),
		tz: z.string().max(64).optional(),
	}),
	z.object({op: z.literal('both'), taskIds: z.array(TaskIdSchema).min(2).max(3), tz: z.string().max(64).optional()}),
	z.object({op: z.literal('dismiss'), tz: z.string().max(64).optional()}),
]);
export type AgentTimeTasksRequest = z.infer<typeof AgentTimeTasksRequestSchema>;

/** The first line of each turn's prompt, read live from the machine for the Turns view (never kept). */
export const AgentTimePromptsSchema = z.object({prompts: z.record(z.string().max(200), z.string().max(200))});
export type AgentTimePrompts = z.infer<typeof AgentTimePromptsSchema>;

const LogAgentSchema = z.object({
	minutes: z
		.number()
		.int()
		.min(1)
		.max(7 * 24 * 60)
		.optional(),
});
const LogYouSchema = z.object({
	minutes: z
		.number()
		.int()
		.min(1)
		.max(24 * 60),
});

/**
 * Log time, up to the moment the person confirmed. `agent`: the agent time not logged yet (left out
 * or `{}`: as measured; `{minutes}`: the person's edited amount, spread over the same days; null:
 * not this time). `you`: the person's own minutes as one entry (null or left out: not this time).
 */
export const AgentTimeLogRequestSchema = z.object({
	upTo: z.string().max(40),
	tz: z.string().max(64).optional(),
	agent: z
		.object({
			minutes: z
				.number()
				.int()
				.min(1)
				.max(7 * 24 * 60)
				.optional(),
		})
		.nullable()
		.optional(),
	you: z
		.object({
			minutes: z
				.number()
				.int()
				.min(1)
				.max(24 * 60),
		})
		.nullable()
		.optional(),
	/**
	 * Per task (the chat's tasks): `agent` and `you` as above, for that task's share. Left out: every
	 * task, as measured, with `agent` / `you` above spread over the tasks by their shares.
	 */
	tasks: z
		.array(
			z.object({
				taskId: TaskIdSchema,
				agent: LogAgentSchema.nullable().optional(),
				you: LogYouSchema.nullable().optional(),
			}),
		)
		.max(10)
		.optional(),
});
export type AgentTimeLogRequest = z.infer<typeof AgentTimeLogRequestSchema>;

/** Set the person's own minutes on the chat; null goes back to the estimate. */
export const AgentTimeYouRequestSchema = z.object({
	minutes: z
		.number()
		.int()
		.min(0)
		.max(100 * 60)
		.nullable(),
	tz: z.string().max(64).optional(),
});
export type AgentTimeYouRequest = z.infer<typeof AgentTimeYouRequestSchema>;

/** Tweak the estimate's knobs for every chat; null goes back to the defaults. */
export const AgentEstimateKnobsRequestSchema = z.object({knobs: AgentEstimateKnobsSchema.nullable()});
export type AgentEstimateKnobsRequest = z.infer<typeof AgentEstimateKnobsRequestSchema>;
export const AgentEstimateKnobsResponseSchema = z.object({knobs: AgentEstimateKnobsSchema});
export type AgentEstimateKnobsResponse = z.infer<typeof AgentEstimateKnobsResponseSchema>;

export const AgentTimeClientOptionSchema = z.object({
	id: z.string().max(200),
	name: z.string().max(120),
	/** A saved client, a ClickUp space, or a name used for agent time before. */
	kind: z.enum(['client', 'space', 'used']),
	/** The person's agent chats with this client. */
	chats: z.number().int().nonnegative(),
});
export type AgentTimeClientOption = z.infer<typeof AgentTimeClientOptionSchema>;
export const AgentTimeClientOptionsSchema = z.object({options: z.array(AgentTimeClientOptionSchema).max(500)});
export type AgentTimeClientOptions = z.infer<typeof AgentTimeClientOptionsSchema>;

/** Error codes of the time routes (beside the Agents ones). */
export const AGENT_TIME_ERROR_CODES = {
	clickupNotConnected: 'CLICKUP_NOT_CONNECTED',
	noTask: 'NO_TASK',
	nothingToLog: 'NOTHING_TO_LOG',
	clickupFailed: 'CLICKUP_FAILED',
	busy: 'BUSY',
	/** Time was logged to that task (or turn): it stays. */
	logged: 'TASK_LOGGED',
} as const;
